import React, { Component, Fragment } from 'react'
import type { ReactElement, ReactNode } from 'react'

import * as Sentry from '@sentry/react'
import type { AxiosError } from 'axios'
import type { GraphQLFormattedError } from 'graphql'
import { GraphQLError } from 'graphql'

import type { LutherException } from '@acre/graphql'

import { registerShowError } from './errorBus'
import { errorMessages, formatErrorMessage } from './errorHandler'
import { chooseSecurityViolationMessage } from './securityViolation/securityViolation'

type Props = {
  children: ReactElement | ReactElement[]
  alertSlot: React.ElementType
  alertId?: string
}

type State = {
  error: ReactNode | null
  sentryMessage: string | null
}

export default class ErrorProvider extends Component<Props, State> {
  static showError: (err: GraphQLFormattedError | AxiosError | Error) => void
  static showErrors: (err: (Readonly<GraphQLFormattedError> | AxiosError | Error)[]) => void
  static showErrorMessage: (content: ReactNode, sentryMessage?: string) => void
  static hideError: () => void

  static defaultProps = {
    alertId: 'globalError',
  }

  constructor(props: Props) {
    super(props)
    ErrorProvider.showError = this._show
    ErrorProvider.showErrors = this._showMany
    ErrorProvider.showErrorMessage = this._showMessage
    ErrorProvider.hideError = this._hide
    registerShowError(this._show)

    this.state = {
      error: null,
      sentryMessage: null,
    }
  }

  _show = (err: GraphQLFormattedError | AxiosError | Error) => {
    const errorMessage = parseError(err)

    this.setState({ error: errorMessage, sentryMessage: null })
  }

  _showMany = (errors: (GraphQLFormattedError | AxiosError | Error)[]) => {
    const collectedErrors = errors.reduce(
      (acc, error) => {
        if (!error?.message) {
          return acc
        }

        if (error?.message in acc) {
          acc[error?.message].count += 1
        } else {
          const parsedError = parseError(error) || 'Unknown error'
          acc[parsedError] = { count: 1, error }
        }

        return acc
      },
      {} as Record<string, { count: number; error: GraphQLFormattedError | AxiosError | Error }>,
    )

    const errorMessages = Object.keys(collectedErrors).map((message) => {
      const error = collectedErrors[message]
      return error.count > 1 ? `${message} (${error.count})` : message
    })

    this.setState({ error: errorMessages.join('\r'), sentryMessage: null })
  }

  _showMessage = (content: ReactNode, sentryMessage?: string) => {
    this.setState({ error: content, sentryMessage: sentryMessage ?? null })
  }

  _hide = () => {
    this.setState({ error: null, sentryMessage: null })
  }

  render() {
    const Alert = this.props.alertSlot

    if (this.state.error) {
      const sentryLabel =
        typeof this.state.error === 'string'
          ? this.state.error
          : this.state.sentryMessage ?? 'Rich error content displayed to user'
      Sentry.captureMessage(`Error alert displayed to user: ${sentryLabel}`, 'error')
    }

    return (
      <Fragment>
        {this.props.children}
        {this.state.error && <Alert id={this.props.alertId} onClick={this._hide} label={this.state.error} />}
      </Fragment>
    )
  }
}

const getLutherException = (err: GraphQLFormattedError | AxiosError | Error) => {
  if ('extensions' in err && err.extensions && 'exception' in err.extensions) {
    return err.extensions.exception as LutherException
  }

  if ('originalError' in err && err.originalError && 'type' in (err.originalError as Record<string, unknown>)) {
    return err.originalError as LutherException
  }
}

const parseError = (err: GraphQLFormattedError | AxiosError | Error) => {
  let errorMessage: string | null = null

  const lutherException = getLutherException(err)

  if (lutherException) {
    const type: ExceptionType = lutherException.type
    switch (type) {
      case 'INFRASTRUCTURE':
        errorMessage = formatErrorMessage(errorMessages.infrastructure, {
          id: lutherException.id,
        })
        break
      case 'SERVICE_NOT_AVAILABLE':
        errorMessage = formatErrorMessage(errorMessages.serviceUnavailable, {
          id: lutherException.id,
          description: lutherException.description,
        })
        break
      case 'SECURITY_VIOLATION':
        errorMessage = chooseSecurityViolationMessage(
          lutherException.statusCode || 0,
          lutherException.id,
          lutherException.description,
        )
        break
      case 'BUSINESS':
        errorMessage = formatErrorMessage(errorMessages.business, {
          description: lutherException.description,
        })
        break
      default:
        errorMessage = formatErrorMessage(errorMessages.generic, {
          id: lutherException.id,
        })
    }
  } else if (err instanceof GraphQLError) {
    if (err?.message.includes('insufficient permissions')) {
      errorMessage = errorMessages.unauthorised
    } else {
      errorMessage = err?.message
    }
  } else if (!Object.values(errorMessages).includes(err?.message) && !err?.message) {
    errorMessage = errorMessages.client
  } else {
    errorMessage = err?.message || null
  }

  return errorMessage
}
